import React, { useEffect, useMemo } from 'react';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import FormSelectField from '../../../common/components/formFields/FormSelectField';
import CommonButton from '../../../common/components/CommonButton';
import FormDatePickerField from '../../../common/components/formFields/FormDatePickerField';
import calendarIcon from '../../../assets/img/icons/calendar.svg';
import { CommonButtonVariants } from '../../../constants/CommonButtonVariants';
import { TransactionTypeOptions } from '../../../constants/TransactionTypes';
import { fetchOrganizationLocations } from '../../SettingsActions';

const ALL_LOCATIONS_OPTION = { value: null, label: 'All' };

const initialValues = {
  transactionType: '',
  transactionDateFrom: '',
  transactionDateTo: '',
  location: ALL_LOCATIONS_OPTION,
};

function TransactionForm({ handleSearch }) {
  const dispatch = useDispatch();
  const organizationLocations = useSelector((state) =>
    state.myOrganization.get('organizationLocations'),
  );

  useEffect(() => {
    dispatch(fetchOrganizationLocations());
  }, [dispatch]);

  const hasSingleLocation =
    Array.isArray(organizationLocations) && organizationLocations.length === 1;

  const locationOptions = useMemo(() => {
    const options = (Array.isArray(organizationLocations) ? organizationLocations : []).map(
      (loc) => ({
        value: loc.id,
        label: loc.name || loc.address || loc?.postalCode?.postcode,
      }),
    );
    return hasSingleLocation ? options : [ALL_LOCATIONS_OPTION, ...options];
  }, [organizationLocations, hasSingleLocation]);

  const selectedLocation = hasSingleLocation ? locationOptions[0] : ALL_LOCATIONS_OPTION;

  const initialFormValues = useMemo(
    () => ({ ...initialValues, location: selectedLocation }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedLocation.value, selectedLocation.label],
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialFormValues}
      onSubmit={handleSearch}
      validateOnBlur
    >
      {() => (
        <Form noValidate>
          <Row className="d-flex align-items-center">
            <Col md={12}>
              <Row className="d-flex justify-content-between align-items-end">
                <Col md={3}>
                  <FormSelectField
                    name="location"
                    label="Location"
                    options={locationOptions}
                    disabled={hasSingleLocation}
                    labelVariant="bordered"
                  />
                </Col>
                <Col md={3}>
                  <FormSelectField
                    name="transactionType"
                    label="Transaction Type"
                    options={TransactionTypeOptions}
                    labelVariant="bordered"
                  />
                </Col>
                <Col md={3}>
                  <FormDatePickerField
                    name="transactionDateFrom"
                    label="Date - From"
                    placeholder="DD/MM/YYYY"
                    icon={calendarIcon}
                    iconClassName="calendar-icon"
                    labelVariant="bordered"
                    closeBtn
                  />
                </Col>
                <Col md={3}>
                  <FormDatePickerField
                    name="transactionDateTo"
                    label="Date - To"
                    placeholder="DD/MM/YYYY"
                    icon={calendarIcon}
                    iconClassName="calendar-icon"
                    labelVariant="bordered"
                    closeBtn
                  />
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <CommonButton
                    className="transaction-search-button mb-3"
                    type="submit"
                    label="Search"
                    variant={CommonButtonVariants.OUTLINED_RED}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );
}

TransactionForm.propTypes = {
  handleSearch: PropTypes.func.isRequired,
};

export default TransactionForm;
